import { nextTick, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue'
import { gsap } from 'gsap'
import { getActiveContext } from '~/components/core/Page/context'

export const useHorizontalScroll = (args) => {
	const scrollContainerRef = args.container
	const customScrollbarRef = args.scrollbar
	const scrollThumbRef = args.thumb
	const onScrollEnd = args.onScrollEnd
	const enabled = args.enabled ?? ref(true)
	const INTENT_THRESHOLD_PX = 8
	const HORIZONTAL_BIAS = 1.1
	const DRAG_EASE_DURATION = args.dragEaseDuration ?? 0.18
	const INERTIA_MULTIPLIER = args.inertiaMultiplier ?? 420
	const INERTIA_MAX_DISTANCE = args.inertiaMaxDistance ?? 900
	const INERTIA_MIN_VELOCITY = args.inertiaMinVelocity ?? 0.08
	const PASSIVE_TOUCH_OPTIONS = { passive: true }
	const NON_PASSIVE_TOUCH_OPTIONS = { passive: false }

	const isDragging = ref(false)
	const scrollTimeout = ref(null)
	const touchGesture = {
		startX: 0,
		startY: 0,
		startScrollLeft: 0,
		axis: null,
		isLocked: false,
		didHorizontalDrag: false,
		lastX: 0,
		lastTime: 0,
		velocityX: 0,
	}
	let stopEnabledWatcher = null
	let isBound = false
	let scrollLeftQuickTo = null
	let scrollLeftQuickToContainer = null
	let momentumTween = null

	const getPageX = (event) => event.pageX ?? event.touches?.[0]?.pageX ?? event.changedTouches?.[0]?.pageX ?? 0

	const getElements = () => {
		const container = scrollContainerRef.value
		const scrollbar = customScrollbarRef.value
		const thumb = scrollThumbRef.value
		const inner = container?.querySelector('.inner')

		return { container, scrollbar, thumb, inner }
	}

	const clampScrollLeft = (value, container) => {
		if (!container) return 0
		const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
		return Math.max(0, Math.min(value, maxScrollLeft))
	}

	const killMomentum = () => {
		momentumTween?.kill()
		momentumTween = null
	}

	const killSmoothDragTween = () => {
		scrollLeftQuickTo?.tween?.kill()
		scrollLeftQuickTo = null
		scrollLeftQuickToContainer = null
	}

	const setScrollLeft = (value, { smooth = false } = {}) => {
		const { container } = getElements()
		if (!container) return

		const nextValue = clampScrollLeft(value, container)
		if (!smooth) {
			container.scrollLeft = nextValue
			return
		}

		if (scrollLeftQuickToContainer !== container || !scrollLeftQuickTo) {
			scrollLeftQuickToContainer = container
			scrollLeftQuickTo = gsap.quickTo(container, 'scrollLeft', {
				duration: DRAG_EASE_DURATION,
				ease: 'power2.out',
				overwrite: true,
			})
		}

		scrollLeftQuickTo(nextValue)
	}

	const updateThumbPosition = () => {
		const { container, scrollbar, thumb, inner } = getElements()

		if (!container || !scrollbar || !thumb || !inner) return

		container.clientWidth >= inner.clientWidth ? (scrollbar.style.display = 'none') : (scrollbar.style.display = 'flex')

		const containerScrollWidth = container.scrollWidth
		const containerClientWidth = container.clientWidth
		const scrollbarClientWidth = scrollbar.clientWidth
		const maxScrollLeft = containerScrollWidth - containerClientWidth

		if (containerScrollWidth <= 0 || scrollbarClientWidth <= 0 || maxScrollLeft <= 0) {
			thumb.style.left = '0px'
			return
		}

		const thumbWidth = (containerClientWidth / containerScrollWidth) * scrollbarClientWidth
		thumb.style.width = `${thumbWidth}px`

		const scrollLeft = container.scrollLeft
		const maxThumbLeft = scrollbarClientWidth - thumbWidth

		const thumbLeft = (scrollLeft / maxScrollLeft) * maxThumbLeft
		thumb.style.left = `${thumbLeft}px`
	}

	const handleScroll = () => {
		updateThumbPosition()

		if (onScrollEnd) {
			clearTimeout(scrollTimeout.value)
			scrollTimeout.value = setTimeout(() => {
				onScrollEnd()
			}, 300)
		}
	}

	const handleMouseDown = () => {
		const { container, scrollbar, thumb } = getElements()
		if (!container || !scrollbar || !thumb) return

		killMomentum()
		isDragging.value = true
		document.body.style.userSelect = 'none'
	}

	const handleMouseUp = () => {
		isDragging.value = false
		document.body.style.userSelect = ''
	}

	const handleMouseMove = (e) => {
		if (!isDragging.value) return
		const { container, scrollbar, thumb } = getElements()
		if (!container || !scrollbar || !thumb) return

		const x = getPageX(e) - scrollbar.offsetLeft
		const thumbWidth = thumb.clientWidth
		const maxScrollLeft = container.scrollWidth - container.clientWidth
		const ratioDenominator = scrollbar.clientWidth - thumbWidth

		if (ratioDenominator <= 0 || maxScrollLeft <= 0) return

		const scrollRatio = (x - thumbWidth / 2) / ratioDenominator
		setScrollLeft(scrollRatio * maxScrollLeft)
	}

	const handleScrollbarClick = (e) => {
		const { container, scrollbar, thumb } = getElements()
		if (!container || !scrollbar || !thumb) return

		const maxThumbLeft = scrollbar.clientWidth - thumb.clientWidth

		const clickPosition = getPageX(e) - scrollbar.offsetLeft - thumb.clientWidth / 2
		const thumbLeft = Math.min(Math.max(clickPosition, 0), maxThumbLeft)

		const maxScrollLeft = container.scrollWidth - container.clientWidth
		if (maxScrollLeft <= 0 || maxThumbLeft <= 0) return

		const scrollRatio = thumbLeft / maxThumbLeft

		container.scrollTo({
			left: scrollRatio * maxScrollLeft,
			behavior: 'smooth',
		})
	}

	const resetTouchGesture = (preserveHorizontalDrag = false) => {
		touchGesture.startX = 0
		touchGesture.startY = 0
		touchGesture.startScrollLeft = 0
		touchGesture.axis = null
		touchGesture.isLocked = false

		if (!preserveHorizontalDrag) {
			touchGesture.didHorizontalDrag = false
		}
	}

	const handleTouchStart = (event) => {
		const { container } = getElements()
		const touch = event.touches?.[0]
		if (!container || !touch) return

		killMomentum()
		killSmoothDragTween()
		touchGesture.startX = touch.clientX
		touchGesture.startY = touch.clientY
		touchGesture.startScrollLeft = container.scrollLeft
		touchGesture.axis = null
		touchGesture.isLocked = false
		touchGesture.didHorizontalDrag = false
		touchGesture.lastX = touch.clientX
		touchGesture.lastTime = performance.now()
		touchGesture.velocityX = 0
	}

	const handleTouchMove = (event) => {
		const { container } = getElements()
		const touch = event.touches?.[0]
		if (!container || !touch) return

		const deltaX = touch.clientX - touchGesture.startX
		const deltaY = touch.clientY - touchGesture.startY
		const absDeltaX = Math.abs(deltaX)
		const absDeltaY = Math.abs(deltaY)

		if (!touchGesture.axis && Math.max(absDeltaX, absDeltaY) < INTENT_THRESHOLD_PX) {
			event.lenisStopPropagation = true
			if (event.cancelable) {
				event.preventDefault()
			}
			return
		}

		if (!touchGesture.axis) {
			touchGesture.axis = absDeltaX > absDeltaY * HORIZONTAL_BIAS ? 'x' : 'y'
			touchGesture.isLocked = touchGesture.axis === 'x'
		}

		if (touchGesture.axis !== 'x') return

		touchGesture.didHorizontalDrag = true
		event.lenisStopPropagation = true

		if (event.cancelable) {
			event.preventDefault()
		}

		const now = performance.now()
		const elapsed = Math.max(1, now - touchGesture.lastTime)
		const frameDeltaX = touch.clientX - touchGesture.lastX
		const instantaneousVelocity = -frameDeltaX / elapsed
		touchGesture.velocityX = touchGesture.velocityX * 0.8 + instantaneousVelocity * 0.2
		touchGesture.lastX = touch.clientX
		touchGesture.lastTime = now

		setScrollLeft(touchGesture.startScrollLeft - deltaX, { smooth: true })
	}

	const handleTouchEnd = () => {
		const { container } = getElements()
		if (!container) {
			resetTouchGesture(true)
			return
		}

		if (touchGesture.didHorizontalDrag && Math.abs(touchGesture.velocityX) >= INERTIA_MIN_VELOCITY) {
			const projectedDistance = gsap.utils.clamp(-INERTIA_MAX_DISTANCE, INERTIA_MAX_DISTANCE, touchGesture.velocityX * INERTIA_MULTIPLIER)
			const targetScrollLeft = clampScrollLeft(container.scrollLeft + projectedDistance, container)
			const distance = Math.abs(targetScrollLeft - container.scrollLeft)
			const duration = gsap.utils.clamp(0.35, 1.1, distance / 900)

			killMomentum()
			momentumTween = gsap.to(container, {
				scrollLeft: targetScrollLeft,
				duration,
				ease: 'power3.out',
				overwrite: true,
				onComplete: () => {
					momentumTween = null
				},
			})
		}

		resetTouchGesture(true)
	}

	const handleClickCapture = (event) => {
		if (!touchGesture.didHorizontalDrag) return

		touchGesture.didHorizontalDrag = false
		event.preventDefault()
		event.stopPropagation()
		event.stopImmediatePropagation?.()
	}

	const bind = () => {
		const { container, thumb } = getElements()
		if (!container || !thumb || isBound) return

		const context = getActiveContext()
		const loader = context?.loader

		container.addEventListener('scroll', handleScroll)
		container.addEventListener('touchstart', handleTouchStart, PASSIVE_TOUCH_OPTIONS)
		container.addEventListener('touchmove', handleTouchMove, NON_PASSIVE_TOUCH_OPTIONS)
		container.addEventListener('touchend', handleTouchEnd)
		container.addEventListener('touchcancel', handleTouchEnd)
		container.addEventListener('click', handleClickCapture, true)
		thumb.addEventListener('mousedown', handleMouseDown)
		thumb.addEventListener('touchstart', handleMouseDown)
		document.addEventListener('mouseup', handleMouseUp)
		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('touchend', handleMouseUp)
		document.addEventListener('touchmove', handleMouseMove)
		isBound = true

		if (loader?.ready) {
			loader.ready.then(() => nextTick(() => updateThumbPosition()))
			return
		}

		nextTick(() => updateThumbPosition())
	}

	const unbind = () => {
		const { container, thumb } = getElements()
		if (!isBound) return

		container?.removeEventListener('scroll', handleScroll)
		container?.removeEventListener('touchstart', handleTouchStart, PASSIVE_TOUCH_OPTIONS)
		container?.removeEventListener('touchmove', handleTouchMove, NON_PASSIVE_TOUCH_OPTIONS)
		container?.removeEventListener('touchend', handleTouchEnd)
		container?.removeEventListener('touchcancel', handleTouchEnd)
		container?.removeEventListener('click', handleClickCapture, true)
		thumb?.removeEventListener('mousedown', handleMouseDown)
		thumb?.removeEventListener('touchstart', handleMouseDown)
		document.removeEventListener('mouseup', handleMouseUp)
		document.removeEventListener('mousemove', handleMouseMove)
		document.removeEventListener('touchend', handleMouseUp)
		document.removeEventListener('touchmove', handleMouseMove)
		clearTimeout(scrollTimeout.value)
		killMomentum()
		killSmoothDragTween()
		isDragging.value = false
		resetTouchGesture()
		isBound = false
	}

	onMounted(() => {
		stopEnabledWatcher = watch(
			() => !!unref(enabled),
			(nextEnabled) => {
				if (!nextEnabled) {
					unbind()
					return
				}

				nextTick(() => bind())
			},
			{ immediate: true }
		)
	})

	onBeforeUnmount(() => {
		stopEnabledWatcher?.()
		unbind()
	})

	return {
		handleScrollbarClick,
		updateThumbPosition,
	}
}
